import { Send, Loader2, Phone, Mail, Calendar, CreditCard, Clock, MapPin, Paperclip, MessageSquare, CheckCircle2 } from "lucide-react";
import { Container } from "@/components/ui/Container";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Reveal } from "@/components/common/Reveal";
import { useLeadForm } from "@/hooks/useLeadForm";
import { AddToCalendar } from "@/components/sections/AddToCalendar";
import { BUSINESS, SERVICES, contactBg } from "@/utils/constants";

const inputClass =
  "bg-ink/80 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-weld focus:outline-none focus:ring-1 focus:ring-weld transition";

export const Contact = () => {
  const { form, setField, files, setFiles, loading, submitted, submit, reset } = useLeadForm();

  return (
    <section id="contact" data-testid="contact-section" className="section relative overflow-hidden bg-charcoal">
      {/* Blurred real welding photo backdrop */}
      <div className="absolute inset-0">
        <img src={contactBg} alt="" aria-hidden className="w-full h-full object-cover opacity-25 blur-md scale-110" />
        <div className="absolute inset-0 bg-ink/80" />
        <div className="absolute inset-0 blueprint-bg opacity-15" />
      </div>

      <Container className="relative grid lg:grid-cols-2 gap-12 max-w-6xl">
        <Reveal>
          <p className="text-weld-glow font-semibold tracking-[0.25em] uppercase text-sm mb-3">Request A Quote</p>
          <h2 className="font-display text-5xl md:text-6xl text-white">Let's Build It.</h2>
          <p className="text-white/60 mt-5 max-w-md">Tell us about your project and we'll get back to you fast with a free estimate.</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button data-testid="contact-book" href={BUSINESS.bookUrl} target="_blank" rel="noopener noreferrer">
              <Calendar size={16} /> Book Appointment
            </Button>
            <Button data-testid="contact-deposit" variant="outline" href={BUSINESS.depositUrl} target="_blank" rel="noopener noreferrer">
              <CreditCard size={16} /> Pay Deposit
            </Button>
            <Button data-testid="contact-text" variant="outline" href={BUSINESS.smsHref}>
              <MessageSquare size={16} /> Text Us
            </Button>
          </div>

          <div className="mt-5">
            <p className="text-white/40 text-xs uppercase tracking-[0.2em]">Booked? Save a reminder:</p>
            <AddToCalendar />
          </div>

          <div className="mt-8 flex flex-col gap-4">
            <a href={BUSINESS.phoneHref} data-testid="contact-phone" className="flex items-center gap-3 text-white/80 hover:text-weld-glow transition-colors">
              <Phone className="text-weld-glow" size={18} /> <span className="font-semibold">{BUSINESS.phone}</span>
            </a>
            <a href={BUSINESS.emailHref} data-testid="contact-email" className="flex items-center gap-3 text-white/80 hover:text-weld-glow transition-colors break-all">
              <Mail className="text-weld-glow" size={18} /> <span className="font-semibold">{BUSINESS.email}</span>
            </a>
            <div className="flex items-center gap-3 text-white/80"><Clock className="text-weld-glow" size={18} /> {BUSINESS.hours}</div>
            <div className="flex items-center gap-3 text-white/80" data-testid="contact-area"><MapPin className="text-weld-glow" size={18} /> Serving {BUSINESS.area}</div>
          </div>

          <Card className="relative overflow-hidden h-[220px] p-0 mt-8">
            <iframe
              title="Service Area — Atlanta"
              data-testid="contact-map"
              src="https://www.google.com/maps?q=Atlanta,GA&z=9&output=embed"
              className="w-full h-full grayscale contrast-125 opacity-90"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-weld/20 rounded-xl" />
          </Card>
        </Reveal>

        <Reveal delay={0.15}>
          {submitted ? (
            <Card data-testid="quote-success" className="rounded-2xl p-6 md:p-8 flex flex-col gap-4">
              <CheckCircle2 className="text-weld-glow" size={36} />
              <h3 className="font-display text-3xl text-white">Request received.</h3>
              <p className="text-white/60">
                We'll reach out shortly with your free estimate. Left your email? A confirmation with our booking link is on its way. Want a reminder? Save a placeholder to your calendar — we'll confirm the exact time with you.
              </p>
              <AddToCalendar />
              <Button data-testid="send-another" variant="outline" size="sm" onClick={reset} className="self-start mt-2">
                Send another request
              </Button>
            </Card>
          ) : (
          <Card as="form" data-testid="quote-form" onSubmit={submit} className="rounded-2xl p-6 md:p-8 flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="input-name" value={form.name} onChange={setField("name")} placeholder="Full Name *" className={inputClass} />
              <input data-testid="input-phone" value={form.phone} onChange={setField("phone")} placeholder="Phone *" className={inputClass} />
            </div>
            <input data-testid="input-email" value={form.email} onChange={setField("email")} placeholder="Email" type="email" className={inputClass} />
            <select data-testid="input-service" value={form.service} onChange={setField("service")} className={inputClass}>
              <option value="">Select a service</option>
              {SERVICES.map((s) => (
                <option key={s.title} value={s.title}>{s.title}</option>
              ))}
            </select>
            <textarea data-testid="input-message" value={form.message} onChange={setField("message")} placeholder="Tell us about your project..." rows={4} className={`${inputClass} resize-none`} />
            <div>
              <p className="text-white/50 text-xs mb-2">Have a welding or fabrication project? Send us a few photos and details so we can better understand the job.</p>
              <label htmlFor="quote-photos" className="flex items-center justify-center gap-2 cursor-pointer border border-dashed border-white/20 hover:border-weld/50 rounded-lg px-4 py-3 text-white/70 hover:text-weld-glow transition-colors">
                <Paperclip size={16} />
                {files.length ? `${files.length} photo${files.length > 1 ? "s" : ""} selected` : "Add project photos (optional)"}
              </label>
              <input
                id="quote-photos"
                data-testid="input-photos"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
            </div>
            {/* Honeypot — invisible to humans, bots fill it and get silently dropped */}
            <div className="absolute -left-[9999px] -top-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
              <label htmlFor="quote-website">Website</label>
              <input
                id="quote-website"
                data-testid="input-website"
                type="text"
                value={form.website}
                onChange={setField("website")}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            <Button data-testid="submit-quote" type="submit" size="lg" disabled={loading} className="disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
              {loading ? "Sending..." : "Request Quote"}
            </Button>
          </Card>
          )}
        </Reveal>
      </Container>
    </section>
  );
};
